import {
	ChevronDownIcon,
	DropdownMenu,
	IconButton,
	UserLoggedInIcon,
	UserProfileIcon,
} from "@au-schein-de/au-ui-library";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate, useRouteLoaderData } from "react-router";
import { documentUrls } from "~/constants/document-urls.constants";
import { redirectToSsoLogin } from "~/tooling/authentication/redirect-to-sso-login";
import type { SessionUser } from "~/tooling/session";
import { captureEvent } from "~/tooling/telemetry/capture-event";
import {
	drAnsayPayInfoOverlayOpenedEvent,
	drAnsayPayLearnMoreClickedEvent,
	modalClosedEvent,
	navigationClickedEvent,
	profileModalOpenedEvent,
	servicesLinkClickedEvent,
} from "~/tooling/telemetry/events";
import type { Route } from "../../routes/+types/_storefront";
import { AuthModals, useAuthModalsQueryState } from "./auth.modals.component";
import { UserProfileMenuItem } from "./auth.user-profile-menu-item.component";
import { DrAnsayPayLearnMoreDialog } from "./dransay-pay-learn-more-dialog.component";
import { DrAnsayPayProfileBanner } from "./dransay-pay-profile-banner.component";
import { ArrowRightIcon } from "./icons/arrow-right-icon";
import { BookIcon } from "./icons/book-icon";
import { GridIcon } from "./icons/grid-icon";
import { LoginRegisterIcon } from "./icons/login-register-icon";
import { RatingsIcon } from "./icons/ratings-icon";

interface UserProfileButtonProps {
	user?: SessionUser;
	desktopIconOnly?: boolean;
	renderModals?: boolean;
}

function FirstPatientGuideSection({ onClick }: { onClick: () => void }) {
	const { t } = useTranslation();
	return (
		<UserProfileMenuItem
			id="user-profile-first-patient-guide"
			testId="user-profile-first-patient-guide"
			icon={<BookIcon className="text-ansay-main" />}
			label={t("userMenu.firstPatientGuide", { ns: "auth" })}
			onClick={onClick}
			marginTop="p-0"
			hasBackground={true}
			textColor="text-gray-700"
		/>
	);
}

function ServiceOffersSection({
	isExpanded,
	onToggle,
	isAuthenticated,
}: {
	isExpanded: boolean;
	onToggle: () => void;
	isAuthenticated: boolean;
}) {
	const { t } = useTranslation();

	const serviceOffers = [
		{
			id: "user-profile-partner-offers",
			titleKey: "userMenu.partnerOffers.title" as const,
			subtitleKey: "userMenu.partnerOffers.subtitle" as const,
			url: documentUrls.partnerOffers,
		},
		{
			id: "user-profile-follow-up-prescriptions",
			titleKey: "userMenu.followUpPrescriptions.title" as const,
			subtitleKey: "userMenu.followUpPrescriptions.subtitle" as const,
			url: documentUrls.followUpPrescriptions,
		},
	];

	const handleServiceOfferClick = (offer: (typeof serviceOffers)[0]) => {
		captureEvent(servicesLinkClickedEvent(offer.id, isAuthenticated));
		window.open(offer.url, "_blank", "noopener,noreferrer");
	};

	return (
		<DropdownMenu.Item className="p-0" onSelect={(e) => e.preventDefault()}>
			<div
				className={`bg-white rounded-lg w-full transition-all ${isExpanded ? "outline-2 outline outline-ansay-main" : ""}`}
			>
				<button
					type="button"
					id="user-profile-service-offers-toggle"
					data-testid="user-profile-service-offers-toggle"
					onClick={onToggle}
					className="group w-full flex items-center justify-between text-body-bold p-2 text-gray-700 gap-3 rounded-lg cursor-pointer transition-colors"
				>
					<div className="flex items-center gap-3">
						<div className="flex items-center justify-center w-10 h-10 bg-ansay-background flex-shrink-0 rounded-lg">
							<GridIcon className="text-ansay-main" />
						</div>
						<span
							className={`text-left group-hover:text-ansay-main transition-colors ${isExpanded ? "text-ansay-main" : ""}`}
						>
							{t("userMenu.serviceOffers", { ns: "auth" })}
						</span>
					</div>
					<span
						className={`transition-transform flex-shrink-0 [&_svg]:size-6 mr-2 group-hover:text-ansay-main ${isExpanded ? "rotate-180" : ""}`}
					>
						<ChevronDownIcon size={6} />
					</span>
				</button>
				{isExpanded && (
					<div className="p-2 space-y-2">
						{serviceOffers.map((offer) => (
							<button
								key={offer.id}
								type="button"
								data-testid={offer.id}
								className="group w-full flex items-center justify-between gap-3 p-3 bg-gray-100 rounded-lg transition-colors cursor-pointer"
								onClick={() => {
									handleServiceOfferClick(offer);
								}}
							>
								<div className="flex-1 text-left" style={{ maxWidth: "275px" }}>
									<div className="text-body-sm-emphasis text-gray-700 group-hover:text-ansay-main transition-colors">
										{t(offer.titleKey, { ns: "auth" })}
									</div>
									<div className="text-caption text-gray-500">
										{t(offer.subtitleKey, { ns: "auth" })}
									</div>
								</div>
								<ArrowRightIcon className="!w-[14px] !h-[9px] text-gray-500 group-hover:text-ansay-main transition-colors" />
							</button>
						))}
					</div>
				)}
			</div>
		</DropdownMenu.Item>
	);
}

function ReviewsButton({
	onClick,
	marginTop,
}: {
	onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
	marginTop: string;
}) {
	const { t } = useTranslation();
	return (
		<UserProfileMenuItem
			id="user-profile-reviews-button"
			testId="user-profile-reviews-button"
			icon={<RatingsIcon className="text-ansay-main" />}
			label={t("userMenu.reviews", { ns: "auth" })}
			pillBadge={t("userMenu.reviewsNewBadge", { ns: "auth" })}
			onClick={onClick}
			marginTop={marginTop}
			hasBackground={true}
			textColor="text-gray-700"
		/>
	);
}

function MyProfileButton({
	user,
	onClick,
	marginTop,
}: {
	user?: SessionUser;
	onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
	marginTop: string;
}) {
	const { t } = useTranslation();
	return (
		<UserProfileMenuItem
			id="user-profile-my-profile-button"
			testId="user-profile-my-profile-button"
			icon={<LoginRegisterIcon className="text-ansay-main" />}
			label={t("myProfile.title", { ns: "profile" })}
			onClick={onClick}
			marginTop={marginTop}
			hasBackground={true}
			textColor="text-gray-700"
		/>
	);
}

function LogoutButton({
	onClick,
}: {
	onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
	const { t } = useTranslation();
	return (
		<UserProfileMenuItem
			id="user-profile-logout"
			testId="user-profile-logout"
			label={t("logout", { ns: "auth" })}
			onClick={onClick}
			marginTop="p-0 mt-6"
			hasBackground={false}
			textColor="text-ansay-main"
			hoverColor="group-hover:[color:var(--color-ansay-light)]"
		/>
	);
}

function LoginButton({
	onClick,
}: {
	onClick: (e: React.MouseEvent<HTMLButtonElement>) => void;
}) {
	const { t } = useTranslation();
	return (
		<UserProfileMenuItem
			id="user-profile-login"
			testId="user-profile-login"
			icon={<LoginRegisterIcon className="text-ansay-main" />}
			label={t("userMenu.loginOrRegister", { ns: "auth" })}
			onClick={onClick}
			marginTop="p-0 mt-6"
			hasBackground={true}
			textColor="text-gray-700"
		/>
	);
}

export function UserProfileButton({
	user,
	desktopIconOnly = false,
	renderModals = true,
}: UserProfileButtonProps) {
	const location = useLocation();
	const [openModal, setOpenModal] = useAuthModalsQueryState();
	const navigate = useNavigate();
	const { t } = useTranslation();
	const [isServiceOffersExpanded, setIsServiceOffersExpanded] = useState(false);
	const [isMenuOpen, setIsMenuOpen] = useState(false);
	const [isDrAnsayPayOverlayOpen, setIsDrAnsayPayOverlayOpen] = useState(false);
	const [isDesktopViewport, setIsDesktopViewport] = useState(true);
	const skipMenuOpenAnalyticsRef = useRef(false);

	useEffect(() => {
		const mql = window.matchMedia("(min-width: 768px)");
		setIsDesktopViewport(mql.matches);
		const handler = (e: MediaQueryListEvent) => setIsDesktopViewport(e.matches);
		mql.addEventListener("change", handler);
		return () => mql.removeEventListener("change", handler);
	}, []);

	const profileDropdownSideOffset = isDesktopViewport ? 24 : 54;

	const storefrontLoaderData =
		useRouteLoaderData<Route.ComponentProps["loaderData"]>(
			"routes/_storefront",
		);
	const enableProfileProductReviewsHub =
		storefrontLoaderData?.enableProfileProductReviewsHub ?? false;
	const handleNavigateToFirstPatientGuide = () => {
		captureEvent(
			navigationClickedEvent(
				"user-profile-first-patient-guide",
				t("userMenu.firstPatientGuide", { ns: "auth" }),
				"sub",
			),
		);
		window.open(
			documentUrls.firstTimePatients,
			"_blank",
			"noopener,noreferrer",
		);
	};

	const handleToggleServiceOffers = () => {
		setIsServiceOffersExpanded(!isServiceOffersExpanded);
		captureEvent(
			navigationClickedEvent(
				"user-profile-service-offers-toggle",
				t("userMenu.serviceOffers", { ns: "auth" }),
				"sub",
			),
		);
	};

	const handleOpenMenuChange = (open: boolean) => {
		setIsMenuOpen(open);
		if (open) {
			if (!skipMenuOpenAnalyticsRef.current) {
				captureEvent(profileModalOpenedEvent(!!user));
			}
			skipMenuOpenAnalyticsRef.current = false;
		} else {
			captureEvent(modalClosedEvent(!!user));
		}
	};

	const handleLearnMoreClick = () => {
		captureEvent(drAnsayPayLearnMoreClickedEvent(!!user));
		setIsMenuOpen(false);
		captureEvent(drAnsayPayInfoOverlayOpenedEvent(!!user));
		setIsDrAnsayPayOverlayOpen(true);
	};

	const handleDrAnsayPayOverlayOpenChange = (open: boolean) => {
		setIsDrAnsayPayOverlayOpen(open);
		if (!open) {
			skipMenuOpenAnalyticsRef.current = true;
			handleOpenMenuChange(true);
		}
	};

	const handleMyProfileClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		const elementId = e.currentTarget.id;
		const label = t("myProfile.title", { ns: "profile" });
		captureEvent(navigationClickedEvent(elementId, label, "sub"));

		if (user) {
			navigate("/profile");
		} else {
			redirectToSsoLogin(location.pathname, location.search);
		}
	};

	const handleReviewsClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		const elementId = e.currentTarget.id;
		const label = t("userMenu.reviews", { ns: "auth" });
		captureEvent(navigationClickedEvent(elementId, label, "sub"));
		navigate("/profile/reviews", { state: { entryPoint: "user_menu" } });
	};

	const handleActionButtonClick = (e: React.MouseEvent<HTMLButtonElement>) => {
		const elementId = e.currentTarget.id;
		const label = user
			? t("logout", { ns: "auth" })
			: t("userMenu.loginOrRegister", { ns: "auth" });
		captureEvent(navigationClickedEvent(elementId, label, "sub"));

		if (user) {
			setOpenModal("logout");
		} else {
			redirectToSsoLogin(location.pathname, location.search);
		}
	};

	return (
		<>
			{(isMenuOpen || isDrAnsayPayOverlayOpen) &&
				createPortal(
					<div
						className="pointer-events-none fixed top-[110px] md:top-[88px] left-0 right-0 bottom-0 z-[19] bg-[rgba(16,17,15,0.5)] transition-opacity duration-150 ease-out"
						data-testid="user-profile-menu-overlay"
						aria-hidden="true"
					/>,
					document.body,
				)}
			<DropdownMenu.Root
				modal={false}
				open={isMenuOpen}
				onOpenChange={handleOpenMenuChange}
			>
				<DropdownMenu.Trigger asChild>
					{user ? (
						<div>
							<IconButton
								id="user-profile-button-mobile"
								shape="square"
								icon={<UserLoggedInIcon.Raw />}
								iconSize={{ base: 5, md: 6 }}
								className={`md:hidden !transition-colors ${isMenuOpen ? "!bg-gray-400" : "!bg-gray-200"}`}
								data-testid="user-profile-button-header-mobile"
								aria-label="User profile menu"
								onClick={(e) => {
									const elementId = e.currentTarget.id;
									captureEvent(navigationClickedEvent(elementId, "", "main"));
								}}
							/>
							{user.firstName && !desktopIconOnly ? (
								<button
									type="button"
									id="user-profile-button-desktop"
									data-testid="user-profile-button-header-desktop"
									aria-label="User profile menu"
									onClick={(e) => {
										const elementId = e.currentTarget.id;
										captureEvent(navigationClickedEvent(elementId, "", "main"));
									}}
									className={`group hidden md:flex h-12 px-3 rounded-xl transition-colors items-center gap-2 cursor-pointer ${isMenuOpen ? "bg-gray-500" : "hover:bg-gray-300 bg-gray-200"}`}
								>
									<UserLoggedInIcon size={6} />
									<span
										className="text-body-sm text-gray-700 whitespace-nowrap"
										suppressHydrationWarning
									>
										{user.firstName}
									</span>
									<span
										className={`transition-transform flex-shrink-0 [&_svg]:size-6 ${isMenuOpen ? "rotate-180" : ""}`}
									>
										<ChevronDownIcon size={6} />
									</span>
								</button>
							) : (
								<IconButton
									id="user-profile-button-desktop"
									shape="square"
									icon={<UserLoggedInIcon.Raw />}
									iconSize={{ base: 5, md: 6 }}
									className={`hidden md:flex !transition-colors ${isMenuOpen ? "!bg-gray-400" : "!bg-gray-200"}`}
									data-testid="user-profile-button-header-desktop"
									aria-label="User profile menu"
									onClick={(e) => {
										const elementId = e.currentTarget.id;
										captureEvent(navigationClickedEvent(elementId, "", "main"));
									}}
								/>
							)}
						</div>
					) : (
						<IconButton
							id="user-profile-button"
							shape="square"
							icon={<UserProfileIcon.Raw />}
							iconSize={{ base: 5, md: 6 }}
							className={`!transition-colors !cursor-pointer ${isMenuOpen ? "!bg-gray-400" : ""}`}
							data-testid="user-profile-button-header"
							onClick={(e) => {
								const elementId = e.currentTarget.id;
								captureEvent(navigationClickedEvent(elementId, "", "main"));
							}}
						/>
					)}
				</DropdownMenu.Trigger>
				<DropdownMenu.Content
					side="bottom"
					align="end"
					sideOffset={profileDropdownSideOffset}
					className="bg-gray-100 border-none shadow-2xl p-4 w-screen md:w-[376px] space-y-2 rounded-b-[24px] md:rounded-xl overflow-hidden"
				>
					<DrAnsayPayProfileBanner onLearnMoreClick={handleLearnMoreClick} />
					<FirstPatientGuideSection
						onClick={handleNavigateToFirstPatientGuide}
					/>
					<ServiceOffersSection
						isExpanded={isServiceOffersExpanded}
						onToggle={handleToggleServiceOffers}
						isAuthenticated={!!user}
					/>
					{user && (
						<MyProfileButton
							user={user}
							onClick={handleMyProfileClick}
							marginTop="p-0 mt-6"
						/>
					)}
					{enableProfileProductReviewsHub && user ? (
						<ReviewsButton onClick={handleReviewsClick} marginTop="p-0 mt-2" />
					) : null}
					{user ? (
						<LogoutButton onClick={handleActionButtonClick} />
					) : (
						<LoginButton onClick={handleActionButtonClick} />
					)}
				</DropdownMenu.Content>
			</DropdownMenu.Root>

			<DrAnsayPayLearnMoreDialog
				open={isDrAnsayPayOverlayOpen}
				onOpenChange={handleDrAnsayPayOverlayOpenChange}
				overlayClassName="bg-transparent"
			/>

			{renderModals && (
				<AuthModals openModal={openModal} setOpenModal={setOpenModal} />
			)}
		</>
	);
}
